import { IconButton } from '@creditornot/cb-button';
import {
  Download,
  EyeFilled,
  EyeOff,
  MoreHorizontal,
  Pencil,
  PlusCircleFilled,
  Trashbin,
  Upload,
} from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useMutation } from '@tanstack/react-query';
import React, { useState, useContext, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, Menu, SubcategoryCreatePayload } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { ConfirmModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { isDefined } from 'common/utils';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import BulkStatusChangeModal from 'modules/menu/bulk-actions/components/BulkStatusChange';
import { useAddSubcategoriesTracking, useDeleteCategoryTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';
import { flattenCategories } from 'modules/menu/utils';

import BulkAddSubcategoryModal from './BulkAddSubcategoriesModal';
import { AddSubcategoriesFormValues } from './BulkAddSubcategoriesModal/types';
import EditCategoryModal from './EditCategoryModal';
import { EditSubcategoryModal } from './EditSubcategoryModal';
import ExportItemsModal from './ExportItemsModal';
import ImportItemsModal from './ImportItemsModal';
import { reportCategoryDelete, reportCategoryItemsDisable, reportCategoryItemsEnable } from '../../behavioral';
import { MenuPermissionsContext } from '../../components/MenuPermissionsContext';
import { WOLT_SPECIALS_CATEGORY_ID } from '../../items/constants';

type Props = {
  category: Category;
  menuEditorRootUrl?: string;
  menuId: string;
  enableAddSubcategory?: boolean;
  type: 'category' | 'subcategory';
};

const ToggleButton = styled(IconButton).attrs({ variant: 'brand', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
  padding: 16px;
`;

const PopoverItem = styled(MenuActionItem).attrs({ variant: 'black' })`
  & + & {
    margin-block-start: 8px;
  }
`;

const DangerPopoverItem = styled(MenuActionItem).attrs({ variant: 'red' })`
  margin-block-start: 8px;
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  margin-block-start: 8px;
`;

const categoryActionsTranslationVariant = {
  category: {
    title: 'menu.category',
    items: 'menu.category-items',
    exportItems: 'menu.exportItems',
    importItems: 'menu.importItems',
    edit: 'menu.editCategory',
    enable: 'menu.enableCategoryItems',
    disabled: 'menu.disableCategoryItems',
    delete: 'menu.deleteCategory',
    deleteDescription: 'menu.deleteCategoryDescription',
  },
  subcategory: {
    title: 'menu.subcategory.title',
    items: 'menu.category-items',
    exportItems: 'menu.subcategory.export-items',
    importItems: 'menu.subcategory.import-items',
    edit: 'menu.subcategory.edit',
    enable: 'menu.subcategory.enable',
    disabled: 'menu.subcategory.disable',
    delete: 'menu.subcategory.delete',
    deleteDescription: 'menu.subcategory.delete-description',
  },
};

const CategoryActions = ({ category, menuEditorRootUrl, menuId, enableAddSubcategory, type }: Props) => {
  const menuPermissions = useContext(MenuPermissionsContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const history = useHistory();

  const { categories, items, updateMenuData } = useMenuData();

  const [isEnableModalOpen, setIsEnableModalOpen] = useState(false);
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [isBulkAddSubcategoriesModalOpen, setIsBulkAddSubcategoriesModalOpen] = useState(false);
  const [isImportItemsModalOpen, setIsImportItemsModalOpen] = useState(false);
  const [isExportItemsModalOpen, setIsExportItemsModalOpen] = useState(false);
  const trackDeleteCategory = useDeleteCategoryTracking(category.id, type);
  const trackAddSubcategories = useAddSubcategoriesTracking();

  const [show, setShow] = useState(false);

  const handleItemClick = (action: () => void) => () => {
    action();
    setShow(false);
  };

  const { mutate: handleDeleteCategory } = useMutation(
    async () =>
      category.id !== WOLT_SPECIALS_CATEGORY_ID ? api.deleteCategory(menuId, category.id) : Promise.resolve(undefined),
    {
      onMutate: () => {
        setIsDeleteModalOpen(false);
      },
      onError: () => {
        handleDeleteCategoryError();
      },
      onSuccess: updatedMenu => {
        if (updatedMenu) {
          trackDeleteCategory();
          handleDeleteCategorySuccess(updatedMenu);
        }
      },
    },
  );

  const handleDeleteCategoryError = () => {
    toast.error(<FormattedMessage id="errors.deleteCategoryFailed" />);
  };

  const handleDeleteCategorySuccess = (updatedMenu: Menu) => {
    toast.success(
      <FormattedMessage
        id="menu.categoryDeletedSuccess"
        values={{ name: localize(category.name, updatedMenu.primary_language) }}
      />,
    );

    if (menuEditorRootUrl) {
      history.push(menuEditorRootUrl);
    }
    updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
    reportCategoryDelete();
  };

  const { mutate: handleBulkAddSubcategory } = useMutation(
    async (subcategories: AddSubcategoriesFormValues['childCategoriesRecord'][string]) => {
      const data: SubcategoryCreatePayload[] = subcategories.map(subcategory => ({
        name: [{ lang: primaryLanguage, value: subcategory.name, verified: true }],
      }));
      trackAddSubcategories(
        category.id,
        subcategories.map(category => category.name),
      );
      return api.bulkCreateSubcategory(menuId, category.id, data);
    },
    {
      onMutate: () => {
        setIsBulkAddSubcategoriesModalOpen(false);
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
      onSuccess: updatedMenu => {
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
      },
    },
  );

  const itemIds = useMemo(() => {
    return flattenCategories([category])
      .flatMap(category =>
        category.item_bindings.map(itemBinding => items.find(item => item.id === itemBinding.item_id)?.id),
      )
      .filter(isDefined);
  }, [category, items]);

  return (
    <>
      <Popover
        content={
          <PopoverContent>
            <MenuItemGroup title={<FormattedMessage id={categoryActionsTranslationVariant[type].title} />}>
              {menuPermissions.contentEdit && (
                <>
                  <PopoverItem icon={<Pencil />} onClick={handleItemClick(() => setIsEditCategoryModalOpen(true))}>
                    <FormattedMessage id={categoryActionsTranslationVariant[type].edit} />
                  </PopoverItem>
                  {category.id !== WOLT_SPECIALS_CATEGORY_ID && enableAddSubcategory && (
                    <PopoverItem
                      icon={<PlusCircleFilled />}
                      onClick={handleItemClick(() => setIsBulkAddSubcategoriesModalOpen(true))}
                    >
                      <FormattedMessage id="menu.subcategory.add-option" />
                    </PopoverItem>
                  )}
                </>
              )}
              {category.id !== WOLT_SPECIALS_CATEGORY_ID && (
                <DangerPopoverItem icon={<Trashbin />} onClick={handleItemClick(() => setIsDeleteModalOpen(true))}>
                  <FormattedMessage id={categoryActionsTranslationVariant[type].delete} />
                </DangerPopoverItem>
              )}
            </MenuItemGroup>
            <StyledMenuItemGroup title={<FormattedMessage id={categoryActionsTranslationVariant[type].items} />}>
              {menuPermissions.itemExportImport && !category.child_categories.length && (
                <>
                  <PopoverItem icon={<Upload />} onClick={handleItemClick(() => setIsImportItemsModalOpen(true))}>
                    <FormattedMessage id={categoryActionsTranslationVariant[type].importItems} />
                  </PopoverItem>
                  <PopoverItem icon={<Download />} onClick={handleItemClick(() => setIsExportItemsModalOpen(true))}>
                    <FormattedMessage id={categoryActionsTranslationVariant[type].exportItems} />
                  </PopoverItem>
                </>
              )}
              <PopoverItem icon={<EyeFilled />} onClick={handleItemClick(() => setIsEnableModalOpen(true))}>
                <FormattedMessage id={categoryActionsTranslationVariant[type].enable} />
              </PopoverItem>
              <DangerPopoverItem icon={<EyeOff />} onClick={handleItemClick(() => setIsDisableModalOpen(true))}>
                <FormattedMessage id={categoryActionsTranslationVariant[type].disabled} />
              </DangerPopoverItem>
            </StyledMenuItemGroup>
          </PopoverContent>
        }
        offset={4}
        open={show}
        onOpenChange={setShow}
        placement="bottom-end"
        renderArrow={false}
      >
        {({ refProps }) => (
          <ToggleButton tabIndex={-1} {...refProps}>
            <MoreHorizontal />
          </ToggleButton>
        )}
      </Popover>
      {(isEnableModalOpen || isDisableModalOpen) && (
        <BulkStatusChangeModal
          categories={categories || []}
          menuId={menuId}
          onClose={() => {
            setIsEnableModalOpen(false);
            setIsDisableModalOpen(false);
          }}
          onSubmitStart={isEnableModalOpen ? () => reportCategoryItemsEnable() : () => reportCategoryItemsDisable()}
          selectedIds={new Set(itemIds)}
          shownModal={isEnableModalOpen ? 'enable' : 'disable'}
        />
      )}
      <ConfirmModal
        description={<FormattedMessage id={categoryActionsTranslationVariant[type].deleteDescription} />}
        header={<FormattedMessage id={categoryActionsTranslationVariant[type].delete} />}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteCategory}
        open={isDeleteModalOpen}
      />
      <BulkAddSubcategoryModal
        onCancel={() => setIsBulkAddSubcategoriesModalOpen(false)}
        onConfirm={handleBulkAddSubcategory}
        isOpen={isBulkAddSubcategoriesModalOpen}
      />
      {type === 'category' && (
        <EditCategoryModal
          category={category}
          isOpen={isEditCategoryModalOpen}
          onClose={() => setIsEditCategoryModalOpen(false)}
        />
      )}
      {type === 'subcategory' && (
        <EditSubcategoryModal
          category={category}
          isOpen={isEditCategoryModalOpen}
          onClose={() => setIsEditCategoryModalOpen(false)}
        />
      )}
      <ImportItemsModal
        menuId={menuId}
        category={category}
        isOpen={isImportItemsModalOpen}
        onClose={() => setIsImportItemsModalOpen(false)}
      />
      <ExportItemsModal
        category={category}
        isOpen={isExportItemsModalOpen}
        onClose={() => setIsExportItemsModalOpen(false)}
      />
    </>
  );
};

export default CategoryActions;
